package io.github.erkko68.filament.compose.scene

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.remember
import io.github.erkko68.filament.Engine
import io.github.erkko68.filament.Entity
import io.github.erkko68.filament.LightManager
import io.github.erkko68.filament.Scene
import io.github.erkko68.filament.compose.FilamentSceneScope
import io.github.erkko68.filament.compose.LocalFilamentEngine
import io.github.erkko68.filament.compose.noFilamentEngine
import io.github.erkko68.filament.compose.internal.rememberOwned
import io.github.erkko68.filament.compose.internal.setParent
import io.github.erkko68.filament.compose.LocalFilamentScene
import io.github.erkko68.filament.compose.noFilamentScene
import io.github.erkko68.filament.compose.OnFrame
import kotlin.math.sqrt


// ── Public: type-specific parameter groups ────────────────────────────────────

/**
 * Spot / focused-spot cone angles (half-angles in radians).
 * [innerAngle] must be ≤ [outerAngle].
 */
@Immutable
data class SpotCone(
    val innerAngle: Float = 0.5f,
    val outerAngle: Float = 0.6f,
)

/**
 * Sun-disk appearance parameters for [SunLight].
 *
 * @param angularRadius Angular radius of the sun disk **in degrees** (0.25°–20°; the real sun
 *   is ~0.545°, Filament's default is 1.9°).
 * @param haloSize Halo radius as a multiplier of the sun's angular radius.
 * @param haloFalloff Halo falloff exponent (higher = tighter halo).
 */
@Immutable
data class SunParams(
    val angularRadius: Float = 1.9f,
    val haloSize: Float = 10f,
    val haloFalloff: Float = 80f,
)

/**
 * A light's brightness as a value type, bundling the amount with how it's interpreted — one
 * variant per intensity setter on the core [LightManager.Builder], so unit-specific extras
 * (Watts' `efficiency`) are unrepresentable with the other units.
 *
 * Follows the same "correlated params become a value type" idiom as [Projection]/[SpotCone]/
 * [SunParams]. The default everywhere is `LuminousPower(100_000f)`.
 */
@Immutable
sealed interface LightIntensity {
    /**
     * Illuminance in lux for directional lights, luminous power in lumen for point/spot lights —
     * Filament's plain `intensity()` and the most common choice.
     */
    data class LuminousPower(val value: Float) : LightIntensity

    /** Luminous intensity in candela; for directional lights this equals lux. */
    data class Candela(val value: Float) : LightIntensity

    /**
     * Electrical watts × luminous [efficiency] → `683 · efficiency · watts` lumen. Typical
     * efficiencies: incandescent 0.022, halogen 0.07, LED 0.087, fluorescent 0.107 —
     * [efficiency] is required because an idealized 1.0 emitter is rarely what you mean.
     */
    data class Watts(val watts: Float, val efficiency: Float) : LightIntensity

    /** Reference values from Filament's documentation. Match the camera's [Exposure] to them. */
    companion object {
        /** Direct sunlight, for a directional light: 100,000 lx. Pairs with [Exposure.Sunny]. */
        val Sun: LightIntensity = LuminousPower(100_000f)

        /** A household light bulb, for a point or spot light: 800 lm. Pairs with [Exposure.Indoor]. */
        val HouseholdBulb: LightIntensity = LuminousPower(800f)
    }
}

// Per-light shadow config ([ShadowConfig]) and the view-level [Shadows] technique live in Shadows.kt.

// ── Internal: change-detection snapshot ───────────────────────────────────────

internal data class LightSnapshot(
    val type: LightManager.Type,
    val direction: Direction,
    val position: Position,
    val color: LinearColor,
    val intensity: LightIntensity,
    val shadow: ShadowConfig?,
    val castLight: Boolean,
    val falloff: Float,
    val cone: SpotCone,
    val sun: SunParams,
    val lightChannels: Set<Int>,
    val followGroupRotation: Boolean = true,
) {
    // Build-time component creation. `shadow`/`castLight` (and `type`) can only be set here — the
    // runtime LightManager exposes no setter for shadow options or castLight — so a change to any
    // of them rebuilds the component (see the build-keyed effect in LightNode).
    fun buildInto(engine: Engine, entity: Entity) {
        val builder = LightManager.Builder(type)
            .direction(direction.x, direction.y, direction.z)
            .color(color.r, color.g, color.b)
            .castLight(castLight)
            .falloff(falloff)
            .spotLightCone(cone.innerAngle, cone.outerAngle)
            .sunAngularRadius(sun.angularRadius)
            .sunHaloSize(sun.haloSize)
            .sunHaloFalloff(sun.haloFalloff)
        when (val i = intensity) {
            is LightIntensity.LuminousPower -> builder.intensity(i.value)
            is LightIntensity.Candela       -> builder.intensityCandela(i.value)
            is LightIntensity.Watts         -> builder.intensity(i.watts, i.efficiency)
        }
        builder.castShadows(shadow != null)
        if (shadow != null) builder.shadowOptions(shadow.toShadowOptions())
        for (channel in 0..7) builder.lightChannel(channel, channel in lightChannels)
        builder.build(engine, entity)
    }

    // Cheap per-change setters for the dynamic subset (everything except shadow/castLight/type).
    fun applyRuntime(engine: Engine, entity: Entity) {
        val lm = engine.lightManager
        val li = lm.getInstance(entity)
        lm.setColor(li, color.r, color.g, color.b)
        when (val i = intensity) {
            is LightIntensity.LuminousPower -> lm.setIntensity(li, i.value)
            is LightIntensity.Candela       -> lm.setIntensityCandela(li, i.value)
            is LightIntensity.Watts         -> lm.setIntensity(li, i.watts, i.efficiency)
        }
        lm.setDirection(li, direction.x, direction.y, direction.z)
        lm.setFalloff(li, falloff)
        lm.setSpotLightCone(li, cone.innerAngle, cone.outerAngle)
        lm.setSunAngularRadius(li, sun.angularRadius)
        lm.setSunHaloSize(li, sun.haloSize)
        lm.setSunHaloFalloff(li, sun.haloFalloff)
        lm.setShadowCaster(li, shadow != null)
        for (channel in 0..7) lm.setLightChannel(li, channel, channel in lightChannels)

        // Position via the transform alone, so Group hierarchy works: Filament applies it to the light's own
        // position, which stays at the origin.
        val tm = engine.transformManager
        tm.setTransform(
            tm.getInstance(entity),
            floatArrayOf(
                1f, 0f, 0f, 0f,
                0f, 1f, 0f, 0f,
                0f, 0f, 1f, 0f,
                position.x, position.y, position.z, 1f,
            ),
        )
    }
}

// ── Internal: shared scene-graph lifecycle for all typed lights ───────────────

/**
 * Owns the entity/scene/transform lifecycle for a typed light and drives it from [snapshot].
 * Build-time-only params (`type`/`shadow`/`castLight`) rebuild the light component; everything else
 * flows through the cheap runtime setters, gated by `LightSnapshot` value equality.
 */
@Composable
internal fun FilamentSceneScope.LightNode(snapshot: LightSnapshot) {
    val engine = LocalFilamentEngine.current ?: noFilamentEngine()
    val scene  = LocalFilamentScene.current ?: noFilamentScene()
    val parent = LocalParentEntity.current

    // Remembered before the effects below, so it's destroyed after their cleanups run.
    val entity = rememberOwned(engine, create = { engine.entityManager.create() }) { engine.entityManager.destroy(it) }

    // Transform component, independent of light parameters.
    DisposableEffect(entity) {
        val tm = engine.transformManager
        if (!tm.hasComponent(entity)) tm.create(entity)
        onDispose { tm.destroy(entity) }
    }

    GroupVisibleSceneMembership(scene, entity)

    // Build (and rebuild) the light component only when a build-time-only param changes.
    DisposableEffect(entity, snapshot.type, snapshot.shadow, snapshot.castLight) {
        snapshot.buildInto(engine, entity)
        onDispose { engine.lightManager.destroy(entity) }
    }

    // Push setters only when a parameter actually changes — LightSnapshot's value equality gates it.
    DisposableEffect(entity, snapshot) {
        snapshot.applyRuntime(engine, entity)
        onDispose { }
    }

    DisposableEffect(entity, parent) {
        engine.setParent(entity, parent)
        onDispose { }
    }

    // Filament turns a parented light with its Group. Pinning its aim in world space is what takes per-frame
    // work, as the Group can move without a recomposition.
    if (!snapshot.followGroupRotation && parent != null) {
        PinWorldDirection(engine, entity, parent, snapshot)
    }
}

/** Keeps a light in [scene] while every enclosing [Group] is visible: hiding a Group turns its lights off too. */
@Composable
private fun GroupVisibleSceneMembership(scene: Scene, entity: Entity) {
    val visible = LocalGroupVisible.current
    DisposableEffect(entity, visible) {
        if (visible) scene.addEntity(entity)
        onDispose { if (visible) scene.remove(entity) }
    }
}

@Composable
private fun PinWorldDirection(engine: Engine, entity: Entity, parent: Entity, snapshot: LightSnapshot) {
    val world = remember { FloatArray(16) }
    OnFrame {
        val tm = engine.transformManager
        if (!tm.hasComponent(parent)) return@OnFrame
        tm.getWorldTransform(tm.getInstance(parent), world)
        val d = pinnedLocalDirection(world, snapshot.direction) ?: return@OnFrame
        val lm = engine.lightManager
        lm.setDirection(lm.getInstance(entity), d.x, d.y, d.z)
    }
}

/**
 * The local direction that ends up as [direction] in world space under the column-major [world] transform.
 * Filament transforms directions by the cofactor matrix, det(M)·M⁻ᵀ, which Mᵀ undoes up to the sign of det(M).
 * Null for a degenerate transform.
 */
internal fun pinnedLocalDirection(world: FloatArray, direction: Direction): Direction? {
    val d = direction
    val x = world[0] * d.x + world[1] * d.y + world[2] * d.z
    val y = world[4] * d.x + world[5] * d.y + world[6] * d.z
    val z = world[8] * d.x + world[9] * d.y + world[10] * d.z
    val det = world[0] * (world[5] * world[10] - world[9] * world[6]) -
        world[4] * (world[1] * world[10] - world[9] * world[2]) +
        world[8] * (world[1] * world[6] - world[5] * world[2])
    val len = sqrt(x * x + y * y + z * z) * if (det < 0f) -1f else 1f
    return if (len != 0f && len.isFinite()) Direction(x / len, y / len, z / len) else null
}

// ── Public: type-specific light composables ───────────────────────────────────

/**
 * A directional light — parallel rays from infinitely far (the classic "sun" for shading, without
 * the visible disk; use [SunLight] for that). Only directional and spot lights can cast shadows.
 *
 * ```kotlin
 * DirectionalLight(
 *     direction = Direction(0.3f, -1f, -0.5f),
 *     intensity = LightIntensity.LuminousPower(100_000f),   // lux
 *     shadow    = ShadowConfig(mapSize = 2048, cascades = 3),
 * )
 * ```
 *
 * @param direction Light direction in world space (need not be a unit vector).
 * @param color Linear-sRGB colour; channels may exceed 1 for an over-bright tint.
 * @param intensity Brightness and its unit as one value ([LightIntensity]) — lux/lumen, candela,
 *   or watts + efficiency.
 * @param shadow Shadow-map config ([ShadowConfig]), or null for no shadows.
 * @param castLight Whether the light emits illumination (false = shadow-only).
 * @param lightChannels Which channels (0–7) this light affects; a renderable is lit only if it
 *   shares an enabled channel. Channel 0 is the default.
 * @param followGroupRotation When inside a [Group], [direction] turns with the group, matching how
 *   meshes rotate with it (e.g. a sun rig that tilts with its parent). On by default; set false to keep
 *   the light's aim fixed in world space, which re-aims it every frame.
 */
@Composable
fun FilamentSceneScope.DirectionalLight(
    direction: Direction = Direction(0.3f, -1f, -0.5f),
    color: LinearColor = LinearColor(1f, 1f, 1f),
    intensity: LightIntensity = LightIntensity.LuminousPower(100_000f),
    shadow: ShadowConfig? = null,
    castLight: Boolean = true,
    lightChannels: Set<Int> = setOf(0),
    followGroupRotation: Boolean = true,
) = LightNode(
    LightSnapshot(LightManager.Type.DIRECTIONAL, direction, Position(0f), color, intensity,
        shadow, castLight, falloff = 10f, cone = SpotCone(),
        sun = SunParams(), lightChannels = lightChannels, followGroupRotation = followGroupRotation),
)

/**
 * A directional light that also draws a sun disk and halo in the sky ([LightManager.Type.SUN]).
 * Same shading as [DirectionalLight] plus the visible disk configured by [sun].
 *
 * @param direction Direction the sunlight travels in world space (need not be a unit vector).
 * @param color Linear-sRGB colour of the light.
 * @param intensity Brightness and its unit as one value ([LightIntensity]).
 * @param sun Sun-disk appearance — angular radius and halo ([SunParams]).
 * @param shadow Shadow-map config ([ShadowConfig]), or null for no shadows.
 * @param castLight Whether the light emits illumination (false = shadow-only).
 * @param lightChannels Channels (0–7) this light affects; channel 0 is the default.
 * @param followGroupRotation When inside a [Group], [direction] turns with the group, matching how
 *   meshes rotate with it. On by default.
 */
@Composable
fun FilamentSceneScope.SunLight(
    direction: Direction = Direction(0.3f, -1f, -0.5f),
    color: LinearColor = LinearColor(1f, 1f, 1f),
    intensity: LightIntensity = LightIntensity.LuminousPower(100_000f),
    sun: SunParams = SunParams(),
    shadow: ShadowConfig? = null,
    castLight: Boolean = true,
    lightChannels: Set<Int> = setOf(0),
    followGroupRotation: Boolean = true,
) = LightNode(
    LightSnapshot(LightManager.Type.SUN, direction, Position(0f), color, intensity,
        shadow, castLight, falloff = 10f, cone = SpotCone(), sun = sun,
        lightChannels = lightChannels, followGroupRotation = followGroupRotation),
)

/**
 * A point light — emits from [position] in all directions, intensity diminishing with the inverse
 * square law out to [falloff].
 *
 * **Point lights cannot cast shadows.** A shadow map is a single depth texture rendered from one
 * projection; a point light's 360° emission won't fit one projection (it would need a 6-face cube
 * map), so Filament — being performance-focused — doesn't implement it. For shadow-casting
 * omnidirectional-style lighting, use a wide-cone [FocusedSpotLight] (a cone fits one projection).
 *
 * @param position World position; driven via a transform, so the light follows an enclosing [Group].
 * @param color Linear-sRGB colour of the light.
 * @param intensity Brightness and its unit as one value ([LightIntensity]).
 * @param falloff Sphere-of-influence radius in world units; minimize overlap for performance.
 * @param castLight Whether the light emits illumination (false = no contribution).
 * @param lightChannels Channels (0–7) this light affects; channel 0 is the default.
 */
@Composable
fun FilamentSceneScope.PointLight(
    position: Position = Position(0f, 2f, 0f),
    color: LinearColor = LinearColor(1f, 1f, 1f),
    intensity: LightIntensity = LightIntensity.LuminousPower(100_000f),
    falloff: Float = 10f,
    castLight: Boolean = true,
    lightChannels: Set<Int> = setOf(0),
) = LightNode(
    LightSnapshot(LightManager.Type.POINT, Direction(0f, -1f, 0f), position, color, intensity,
        shadow = null, castLight = castLight, falloff = falloff,
        cone = SpotCone(), sun = SunParams(), lightChannels = lightChannels),
)

/**
 * A spot light — emits from [position] along [direction] within a [cone], decoupling the outer cone
 * from total illumination (easier to tweak than [FocusedSpotLight]). Can cast shadows.
 *
 * @param position World position; driven via a transform, so the light follows an enclosing [Group].
 * @param direction Aim direction in world space (need not be a unit vector).
 * @param color Linear-sRGB colour of the light.
 * @param intensity Brightness and its unit as one value ([LightIntensity]).
 * @param falloff Sphere-of-influence radius in world units.
 * @param cone Inner/outer cone half-angles in radians ([SpotCone]).
 * @param shadow Shadow-map config ([ShadowConfig]), or null for no shadows.
 * @param castLight Whether the light emits illumination (false = shadow-only).
 * @param lightChannels Channels (0–7) this light affects; channel 0 is the default.
 * @param followGroupRotation When inside a [Group], [direction] turns with the group, matching how
 *   meshes rotate with it. On by default.
 */
@Composable
fun FilamentSceneScope.SpotLight(
    position: Position = Position(0f, 2f, 0f),
    direction: Direction = Direction(0f, -1f, 0f),
    color: LinearColor = LinearColor(1f, 1f, 1f),
    intensity: LightIntensity = LightIntensity.LuminousPower(100_000f),
    falloff: Float = 10f,
    cone: SpotCone = SpotCone(),
    shadow: ShadowConfig? = null,
    castLight: Boolean = true,
    lightChannels: Set<Int> = setOf(0),
    followGroupRotation: Boolean = true,
) = LightNode(
    LightSnapshot(LightManager.Type.SPOT, direction, position, color, intensity,
        shadow, castLight, falloff, cone, sun = SunParams(),
        lightChannels = lightChannels, followGroupRotation = followGroupRotation),
)

/**
 * A physically-correct spot light ([LightManager.Type.FOCUSED_SPOT]) — like [SpotLight], but the
 * outer cone angle affects total illumination (narrowing the cone concentrates the light). Can cast
 * shadows. Parameters match [SpotLight].
 *
 * @param position World position; driven via a transform, so the light follows an enclosing [Group].
 * @param direction Aim direction in world space (need not be a unit vector).
 * @param color Linear-sRGB colour of the light.
 * @param intensity Brightness and its unit as one value ([LightIntensity]).
 * @param falloff Sphere-of-influence radius in world units.
 * @param cone Inner/outer cone half-angles in radians ([SpotCone]); the outer angle scales total output.
 * @param shadow Shadow-map config ([ShadowConfig]), or null for no shadows.
 * @param castLight Whether the light emits illumination (false = shadow-only).
 * @param lightChannels Channels (0–7) this light affects; channel 0 is the default.
 * @param followGroupRotation When inside a [Group], [direction] turns with the group, matching how
 *   meshes rotate with it. On by default.
 */
@Composable
fun FilamentSceneScope.FocusedSpotLight(
    position: Position = Position(0f, 2f, 0f),
    direction: Direction = Direction(0f, -1f, 0f),
    color: LinearColor = LinearColor(1f, 1f, 1f),
    intensity: LightIntensity = LightIntensity.LuminousPower(100_000f),
    falloff: Float = 10f,
    cone: SpotCone = SpotCone(),
    shadow: ShadowConfig? = null,
    castLight: Boolean = true,
    lightChannels: Set<Int> = setOf(0),
    followGroupRotation: Boolean = true,
) = LightNode(
    LightSnapshot(LightManager.Type.FOCUSED_SPOT, direction, position, color, intensity,
        shadow, castLight, falloff, cone, sun = SunParams(),
        lightChannels = lightChannels, followGroupRotation = followGroupRotation),
)

// ── Public: low-level builder escape hatch ────────────────────────────────────

/**
 * Low-level escape hatch: adds a light configured directly through the raw [LightManager.Builder],
 * for cases the typed composables ([DirectionalLight], [SpotLight], …) don't cover. You own the full
 * builder; this composable only manages the entity, scene membership, and (when inside a [Group])
 * the transform so the light follows the group.
 *
 * The component is built once and rebuilt whenever any value in [keys] changes — pass every input
 * your [configure] reads so updates take effect (same convention as [FilamentEffect][io.github.erkko68.filament.compose.FilamentEffect]
 * and [rememberMaterialInstance]). When the light is **not** parented, positioning is via
 * `builder.position(...)`; inside a [Group], it follows the group instead.
 *
 * ```kotlin
 * Light(LightManager.Type.SPOT, intensity) {
 *     position(2f, 3f, 0f); direction(0f, -1f, 0f)
 *     intensity(intensity); spotLightCone(0.3f, 0.5f)
 *     castShadows(true)
 * }
 * ```
 */
@Composable
fun FilamentSceneScope.Light(
    type: LightManager.Type,
    vararg keys: Any?,
    configure: LightManager.Builder.() -> Unit,
) {
    val engine = LocalFilamentEngine.current ?: noFilamentEngine()
    val scene  = LocalFilamentScene.current ?: noFilamentScene()
    val parent = LocalParentEntity.current

    val entity = rememberOwned(engine, create = { engine.entityManager.create() }) { engine.entityManager.destroy(it) }

    DisposableEffect(entity) {
        onDispose {
            val tm = engine.transformManager
            if (tm.hasComponent(entity)) tm.destroy(entity)
        }
    }

    GroupVisibleSceneMembership(scene, entity)

    DisposableEffect(entity, type, *keys) {
        LightManager.Builder(type).apply(configure).build(engine, entity)
        onDispose { engine.lightManager.destroy(entity) }
    }

    // Only create a transform when parented — otherwise leave positioning to builder.position(),
    // which a transform component would override.
    DisposableEffect(entity, parent) {
        engine.setParent(entity, parent)
        onDispose { }
    }
}
